import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { Fragment, useEffect, useMemo, useState } from "react";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled, useTheme } from "@mui/material";
import DiscountIcon from '@mui/icons-material/Discount';
import { useI18N } from "../../context/I18NContext";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from "react-router-dom";
import useWidgetStore from "../../store/useWidgetStore";
import useDataStore from "../../store/useDataStore";
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import { CustomCheckboxElement, CustomTextFieldElement } from "../../layouts/Theme";
import { useVoucher } from "../../hooks/query/useVouchers";
import { useValidateVoucher } from "../../hooks/query/useValidateVoucher";
import { useFlow } from "../../context/FlowContext";
import { useSettings } from "../../context/SettingsContext";
import { FormContainer, FormErrorProvider, useForm } from "react-hook-form-mui";
import { createRow, moneyFormat, subtotal } from "../../helpers/general";
import { appointmentsInOrder, apptypeNamesFor, capacityGroupNamesFrom, capacityLabelFor, resourceNamesFor } from "../../helpers/appointment";
/**
 * A price or a quantity of zero tells the consumer nothing, so leave the cell empty rather than
 * printing "€ 0,-" or a bare 0. The cell itself stays, so the columns keep lining up.
 */
const showMoney = (value, prefix = '') => (value && value > 0 ? `${prefix}${moneyFormat(value)}` : '');
const showQty = (value) => (value && value > 0 ? String(value) : '');
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    verticalAlign: 'top',
    textAlign: 'right',
    minWidth: '5em',
    // A long product/apptype name in the row's other cell has nothing bounding its own width, so
    // the table's auto layout shrinks this column to make room for it - without this, that leaves
    // "€ 75,-" narrower than its own text and wraps it onto two lines instead.
    whiteSpace: 'nowrap'
}));
export default function Cart({ fees, paymentMethod, deposit, canDeposit, useDeposit, setter, locked, canRedeem, onEdit, membershipCoverage, setSubTotal, setErrorValidate, setSuccessValidate, successValidate, errorValidate }) {
    const { t, ts } = useI18N();
    const theme = useTheme();
    const navigate = useNavigate();
    const { showSelectedResource, staticData, hasCheckout } = useSettings();
    const { apptypes, resources, products, } = useDataStore();
    const { booking, consumer, language, cart, setCart, paymentOptions } = useWidgetStore();
    const flow = useFlow();
    const capacityGroupNames = useMemo(() => capacityGroupNamesFrom(flow.views), [flow.views]);
    const formContextVoucher = useForm({});
    const vouchersQuery = useVoucher();
    const validateVoucherQuery = useValidateVoucher(formContextVoucher);
    const { register, getValues, watch, formState: {} } = useForm();
    const [rows, setRows] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [vats, setVats] = useState([]);
    const [invoiceSubtotal, setInvoicesubtotal] = useState(0);
    const [invoiceTotalMinusDiscounts, setInvoiceTotalMinusDiscounts] = useState(0);
    const [chargeTotal, setChargeTotal] = useState(0);
    const [alternativePayment, setAlternativePayment] = useState(false);
    const [giftcards, setGiftcards] = useState();
    const [coupons, setCoupons] = useState();
    const [requiresAdditionalPayment, setRequiresAdditionalPayment] = useState(false);
    const [requiredSubscription, setRequiredSubscription] = useState();
    const [requiredSubscriptions, setRequiredSubscriptions] = useState(0);
    const [requiredSubscriptionsTotal, setRequiredSubscriptionsTotal] = useState(0);
    // How many of the booking's appointments the existing strippenkaart pays in full, when it cannot pay
    // them all. Undefined means "not split": every appointment is paid the one way the method says.
    const [coveredAppointments, setCoveredAppointments] = useState();
    const [invoiceTotal, setInvoiceTotal] = useState(0);
    const [showDetails, setShowDetails] = useState(false);
    const [hasCode, setHasCode] = useState(false);
    const [onlyDeposit, setOnlyDeposit] = useState(false);
    const isLarge = useMediaQuery(theme.breakpoints.up('sm'));
    // What the cart says about a membership that is over its limits:
    //  - 'exceeded': the membership for all appointments, which it cannot pay for. This is what is selected as long
    //    as the consumer has not chosen anything else - that option is shown checked, though it cannot be chosen -
    //    so the appointments over the limit say so, and there is nothing to pay
    //  - 'addPayment': the consumer chose to pay the rest online: those appointments show their price
    //  - 'none': no membership over its limits, or another way of paying was chosen
    const noChoiceYet = !paymentMethod || paymentMethod === 'alternate';
    const membershipMode = !membershipCoverage
        ? 'none'
        : ((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('membership_')) && paymentMethod.endsWith('addPayment'))
            ? 'addPayment'
            : (noChoiceYet ? 'exceeded' : 'none');
    // a value for the effect below, instead of an array that is a new one on every render
    const membershipCoverageKey = membershipCoverage ? membershipCoverage.covered.map((covered) => covered ? '1' : '0').join('') : '';
    // Once "Strippenkaart/Abonnement aanschaffen" is confirmed, paymentMethod moves on to the
    // actual payment provider paying for that purchase (e.g. 'external') - it no longer starts
    // with 'subscription_'/'membership_', even though the appointment is still being covered by
    // what is being bought, not paid for directly. cart.lines carrying a subscription/membership
    // product line is the reliable signal for that (the same check Payment.tsx already uses
    // elsewhere), unlike paymentMethod's current literal value, which by then no longer says so.
    // The backend tags a newly-bought subscription-type product's order line as productType
    // 'subscription' regardless of whether the SubscriptionType underneath it is actually a
    // membership or a punch card - 'membership' was never observed for this "buying new" case in
    // testing, only 'subscription'. So this alone cannot tell a membership purchase from a
    // subscription one; requiredSubscription.type is what does that, see subscriptionKind below.
    const buyingSubscriptionOrMembership = !!cart.lines.find((line) => line.productType === 'subscription' || line.productType === 'membership');
    useEffect(() => {
        if ((paymentOptions === null || paymentOptions === void 0 ? void 0 : paymentOptions.deposit) || (paymentOptions === null || paymentOptions === void 0 ? void 0 : paymentOptions.full)) {
            if (!(paymentOptions === null || paymentOptions === void 0 ? void 0 : paymentOptions.full)) {
                setOnlyDeposit(true);
                if (setter) {
                    setter(true);
                }
            }
            else {
                setOnlyDeposit(false);
                if (setter) {
                    setter(false);
                }
            }
        }
    }, [paymentOptions]);
    watch(() => {
        setHasCode(getValues('code').length > 0);
    }, ['code']);
    useEffect(() => {
        vouchersQuery.refetch();
    }, [booking.appointments]);
    useEffect(() => {
        if (vouchersQuery.data) {
            let vouchers = vouchersQuery.data.result.items;
            let usableGiftcards = vouchers.filter((voucher) => voucher.type === 'giftCard' && voucher.valid);
            let usableVouchers = vouchers.filter((voucher) => voucher.type === 'discount' && voucher.valid && voucher.value > 0);
            setGiftcards(usableGiftcards);
            setCoupons(usableVouchers);
            if (usableVouchers.length > 0) {
                let _discounts = cart.discounts;
                setCart(Object.assign(Object.assign({}, cart), { discounts: _discounts, giftCards: usableGiftcards }));
            }
        }
    }, [vouchersQuery.data]);
    useEffect(() => {
        if (validateVoucherQuery.data && !validateVoucherQuery.isFetching) {
            if (validateVoucherQuery.data.status.status === 'failed') {
                if (validateVoucherQuery.data.status.userMessages) {
                    let _errors = [];
                    _errors.push(validateVoucherQuery.data.status.message);
                    Object.values(validateVoucherQuery.data.status.userMessages).map((error, i) => {
                        _errors.push(error);
                    });
                    setErrorValidate(_errors);
                }
                else {
                    setErrorValidate([validateVoucherQuery.data.status.message]);
                }
            }
            else {
                if (!validateVoucherQuery.data.result.item.valid) {
                    setErrorValidate([validateVoucherQuery.data.result.item.title]);
                }
                else {
                    let item = validateVoucherQuery.data.result.item;
                    if (item.type === "discount" && cart.discounts.length > 0) {
                        setErrorValidate([t("Je kunt maar 1 korting per bestelling toepassen", "payment")]);
                    }
                    else {
                        setSuccessValidate([validateVoucherQuery.data.result.item.title.concat(' ').concat(t('toegepast', 'cartSummary'))]);
                        if (item.type === "discount") {
                            setCart(Object.assign(Object.assign({}, cart), { discounts: [...cart.discounts, validateVoucherQuery.data.result.item] }));
                        }
                        else if (item.type === "giftCard") {
                            let newGiftCard = validateVoucherQuery.data.result.item;
                            newGiftCard.active = true;
                            setCart(Object.assign(Object.assign({}, cart), { giftCards: [...cart.giftCards, newGiftCard], discounts: [...cart.discounts, newGiftCard] }));
                        }
                    }
                    formContextVoucher.setValue('code', '');
                    setHasCode(false);
                }
            }
        }
    }, [validateVoucherQuery.isFetched, validateVoucherQuery.isFetching, validateVoucherQuery.isRefetching]);
    //  2APE QC
    useEffect(() => {
        // we have removed a giftcard use, add it to the list of available giftcards again
        // or we have added a new giftcard code
        if (giftcards && giftcards.length > 0 && cart.giftCards.length != (giftcards === null || giftcards === void 0 ? void 0 : giftcards.length)) {
            // @ts-ignore
            setGiftcards(cart.giftCards);
        }
    }, [cart.giftCards, giftcards]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        let _rows = [];
        let appointmentProductLines = cart.lines.filter((line) => line.productType === 'appointment');
        // Whether the appointment is settled at the counter and this step only pays for the products.
        // The backend leaves it out of the amount to pay in exactly this case
        // (CheckoutController::_paysProductsOnly), so showing a price for it here would be a price the
        // consumer is not being charged. It keeps its price the moment something does pay for it: a
        // subscription or a membership, whether they own it or buy it in this order.
        let payingWithSubscription = !!paymentMethod && (paymentMethod === 'alternate'
            || paymentMethod.startsWith('subscription_')
            || paymentMethod.startsWith('membership_'));
        let buysSubscription = cart.lines.some((line) => ["subscription", "membership"].includes(line.productType));
        let hasPaidProducts = cart.lines.some((line) => { var _a; return line.productType === 'product' && ((_a = line.originalPrice) !== null && _a !== void 0 ? _a : line.price) > 0; });
        // "<provider>_productsOnly" is the same "at the counter" display as an apptype whose payment
        // is 'none' below - a none with a payment, in effect: the appointment is skipped/was never
        // payable online, and this step pays the required product(s) with that provider instead. See
        // CheckoutController::_getCheckout() for where the backend offers this option.
        let payingProductsOnly = !!paymentMethod && paymentMethod.endsWith('_productsOnly');
        // one app type that can be paid makes the whole appointment payable, the way the backend
        // decides it per order line
        let appointmentsAtTheCounter = payingProductsOnly || (((_b = (_a = booking === null || booking === void 0 ? void 0 : booking.appointments) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0
            && booking.appointments.every((appointment) => {
                var _a, _b;
                return ((_b = (_a = appointment.components[0]) === null || _a === void 0 ? void 0 : _a.appointmentTypes) !== null && _b !== void 0 ? _b : []).every((apptypeId) => {
                    let apptype = apptypes.find((apptype) => apptype.id === apptypeId);
                    return (apptype === null || apptype === void 0 ? void 0 : apptype.payment) === 'none';
                });
            }));
        let appointmentIsUnpaid = appointmentsAtTheCounter && hasPaidProducts
            && !payingWithSubscription && !buysSubscription;
        // The cart's appointment lines say which appointment they are by the id the backend gave it, and the
        // booking's own appointments are only ever the picked date and time - they carry no id. So once the
        // cart has lines (after going back and forward again, say) none of them can be matched to an
        // appointment here, and guessing - the first one - showed that one's date on every line. Without a
        // match the appointments are shown the way they are before there is a cart: together, in one block.
        let linesMatchAppointments = appointmentProductLines.length > 0
            && appointmentProductLines.every((line) => { var _a; return (_a = booking === null || booking === void 0 ? void 0 : booking.appointments) === null || _a === void 0 ? void 0 : _a.some((appointment) => appointment.id === line.appointmentId); });
        if (!linesMatchAppointments) {
            if ((booking === null || booking === void 0 ? void 0 : booking.appointments) && (booking === null || booking === void 0 ? void 0 : booking.appointments.length) > 0) {
                // in the order of the appointments in time, not of the order they were added
                const sortedAppointments = appointmentsInOrder(booking.appointments);
                // One row per distinct apptype+resource, not one row for the whole booking: a
                // second appointment ("Nog een afspraak toevoegen") can be under a different
                // apptype with a different price entirely, and averaging or multiplying a single
                // price across every appointment in the order is only ever right when they all
                // happen to share that same price. A group can still hold more than one
                // appointment - e.g. two of the same apptype at different times - and that group's
                // own row correctly shows their combined quantity and price.
                const groups = [];
                sortedAppointments.forEach((_appointment) => {
                    var _a;
                    const apptype = apptypeNamesFor(_appointment, apptypes).join(', ');
                    const resource = showSelectedResource ? resourceNamesFor(_appointment, (_a = staticData === null || staticData === void 0 ? void 0 : staticData.resources) !== null && _a !== void 0 ? _a : []).join(', ') : '';
                    const key = `${apptype}|${resource}`;
                    let group = groups.find((one) => one.key === key);
                    if (!group) {
                        group = { key, apptype, resource, appointments: [] };
                        groups.push(group);
                    }
                    group.appointments.push(_appointment);
                });
                groups.forEach((group) => {
                    if (group.apptype === '')
                        return;
                    // Each appointment's own apptype/resource/capacity - not just the first's, and
                    // not the pickers' live selection either: what it was actually booked with is
                    // what this line has to show.
                    let desc = [];
                    group.appointments.forEach((_appointment) => {
                        desc.push(dayjs(_appointment.date).locale(language).format('dddd D MMMM HH:mm'));
                    });
                    group.appointments.forEach((_appointment) => {
                        var _a;
                        const appTypeNames = apptypeNamesFor(_appointment, apptypes);
                        const resourceNames = showSelectedResource
                            ? resourceNamesFor(_appointment, (_a = staticData === null || staticData === void 0 ? void 0 : staticData.resources) !== null && _a !== void 0 ? _a : [])
                            : [];
                        const capacityText = capacityLabelFor(_appointment, capacityGroupNames, { t, ts }, 'cartSummary');
                        const parts = [appTypeNames.join(', ')];
                        if (resourceNames.length > 0)
                            parts.push(resourceNames.join(', '));
                        if (capacityText !== '')
                            parts.push(capacityText);
                        desc.push(parts.filter((part) => part !== '').join(' - '));
                    });
                    // What is shown at the top: only what is not the same for all of them in this
                    // group. Each appointment says when it is, and for how many; what it was booked
                    // for - the apptype and the resource - is the same for the whole group, so it is
                    // said once, after them.
                    const header = [];
                    group.appointments.forEach((_appointment) => {
                        header.push({ text: dayjs(_appointment.date).locale(language).format('dddd D MMMM HH:mm'), bold: true });
                        const capacityText = capacityLabelFor(_appointment, capacityGroupNames, { t, ts }, 'cartSummary');
                        if (capacityText !== '')
                            header.push({ text: capacityText });
                    });
                    header.push({ text: group.apptype });
                    if (group.resource !== '')
                        header.push({ text: group.resource });
                    // one appointment for the price it costs; several of the SAME apptype for what
                    // all of them add up to - this group never mixes appointments that cost
                    // different amounts, so qty × unit is exact, not an average
                    const totalPrice = group.appointments.reduce((sum, one) => sum + one.price, 0);
                    const quantity = group.appointments.length;
                    _rows.push(Object.assign(Object.assign({}, createRow(desc, appointmentIsUnpaid ? 0 : totalPrice / quantity, quantity, 0, //   21,
                    "appointment", "appointment")), { unpaid: appointmentIsUnpaid, header }));
                });
            }
        }
        appointmentProductLines.filter(() => linesMatchAppointments).map((line) => {
            var _a;
            // One cart line per appointment already (line.appointmentId ties them together) - a
            // row for each, not just the first: an earlier "should always be 1, for now" assumed
            // a single appointment and only ever showed that one, silently dropping every other
            // appointment's line from this view once bookings could hold more than one.
            if (line.productType === 'appointment') {
                let appointment = booking.appointments.find((appointment) => appointment.id === line.appointmentId);
                if (appointment) {
                    let dateStr = dayjs(appointment.date).locale(language).format('dddd D MMMM HH:mm');
                    let appTypeNames = apptypeNamesFor(appointment, apptypes);
                    let resourceNames = resourceNamesFor(appointment, resources);
                    let capacityText = capacityLabelFor(appointment, capacityGroupNames, { t, ts }, 'cartSummary');
                    let desc = [dateStr, appTypeNames.join(', ')];
                    if (resourceNames.length > 0)
                        desc.push(resourceNames.join(', '));
                    if (capacityText !== '')
                        desc.push(capacityText);
                    _rows.push(Object.assign(Object.assign({}, createRow(desc, appointmentIsUnpaid ? 0 : ((_a = line === null || line === void 0 ? void 0 : line.originalPrice) !== null && _a !== void 0 ? _a : line.price), 1, 0, //   21,
                    "appointment", "appointment")), { unpaid: appointmentIsUnpaid }));
                }
            }
        });
        let otherProductLines = cart.lines.filter((line) => line.productType !== 'appointment');
        otherProductLines.map((line) => {
            var _a, _b, _c;
            _rows.push(createRow([
                line.name
            ], (_a = line === null || line === void 0 ? void 0 : line.originalPrice) !== null && _a !== void 0 ? _a : line.price, (_b = line.quantity) !== null && _b !== void 0 ? _b : 1, 0, //   21,
            line.productType, (_c = line.origin) !== null && _c !== void 0 ? _c : "optionalPurchase"));
        });
        setRows(_rows);
        let _subtotal = subtotal(_rows);
        setInvoicesubtotal(_subtotal);
        // A subscription or a membership only ever pays for the appointment, so the products are on top
        // of whatever it costs. Rows of type 'product' only: the subscription itself is a row too, and
        // the branches below already count that one.
        let _productsTotal = subtotal(_rows.filter((row) => row.type === 'product'));
        // if (paymentMethod === 'none' || paymentMethod?.startsWith('membership_')) {
        if (membershipMode === 'exceeded') {
            // nothing is owed: what the membership cannot pay for cannot be paid at all with this choice
            setChargeTotal(0);
            setInvoiceTotal(0);
            setInvoiceTotalMinusDiscounts(0);
            setAlternativePayment(false);
            setShowDetails(false);
        }
        else if (membershipMode === 'addPayment' && membershipCoverage) {
            // the membership pays for what is within its limits, the rest is paid online in full
            let total = membershipCoverage.covered.reduce((sum, covered, i) => { var _a, _b; return sum + (covered ? 0 : ((_b = (_a = booking.appointments[i]) === null || _a === void 0 ? void 0 : _a.price) !== null && _b !== void 0 ? _b : 0)); }, 0) + _productsTotal;
            setChargeTotal(0);
            setInvoiceTotal(total);
            setInvoiceTotalMinusDiscounts(total);
            setAlternativePayment(false);
            setShowDetails(total > 0);
        }
        else if (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('membership_')) {
            let subscription = (_c = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _c === void 0 ? void 0 : _c.find((subscription) => subscription.id === parseInt(paymentMethod.substring(11)));
            // Unlike the subscription_ branch below, this one never recorded which subscription
            // was picked - so once paymentMethod moves past this prefix (to the provider actually
            // paying for it), there was nothing left telling detailLines this is a membership, not
            // a subscription. requiredSubscription.type is what tells them apart once that happens.
            setRequiredSubscription(subscription);
            // requiredSubscriptions/requiredSubscriptionsTotal are what the "picking a
            // subscription/membership to buy" preview row below reads - only when actually buying
            // a new one (no subscriptionId yet), never when paying with one already owned. A
            // membership is always exactly one, unlike a punch card, which may need several to
            // cover every appointment.
            if (!(subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId)) {
                setRequiredSubscriptions(1);
                setRequiredSubscriptionsTotal((_d = subscription === null || subscription === void 0 ? void 0 : subscription.sellPrice) !== null && _d !== void 0 ? _d : 0);
            }
            else {
                setRequiredSubscriptions(0);
                setRequiredSubscriptionsTotal(0);
            }
            setChargeTotal(0);
            let total = (subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId)
                ? 0
                : (_e = (paymentMethod.endsWith('_mandate') ? subscription === null || subscription === void 0 ? void 0 : subscription.initialFee : subscription === null || subscription === void 0 ? void 0 : subscription.sellPrice)) !== null && _e !== void 0 ? _e : 0;
            if (total < 0)
                total = 0;
            total += _productsTotal;
            setInvoiceTotal(total);
            setInvoiceTotalMinusDiscounts(total);
            let parts = [(_f = subscription === null || subscription === void 0 ? void 0 : subscription.title) !== null && _f !== void 0 ? _f : ''];
            if (paymentMethod.endsWith('_mandate'))
                parts.push(t('met machtiging', 'cartSummary'));
            if (paymentMethod.endsWith('_mandate') && (subscription === null || subscription === void 0 ? void 0 : subscription.initialFee))
                parts.push(t('eenmalige kosten', 'cartSummary'));
            setAlternativePayment(parts.join(', '));
            setShowDetails(total > 0 && !(subscription === null || subscription === void 0 ? void 0 : subscription.mandatable));
        }
        else if (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('subscription_')) {
            let subscription = (_g = consumer.subscriptions) === null || _g === void 0 ? void 0 : _g.find((subscription) => subscription.id === parseInt(paymentMethod.substring(13).replace('_addPayment', '').replace('_addSubscription', '')));
            if (subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId) {
                // the state setter below does not read back in this pass, so keep it locally as well
                let _requiresAdditionalPayment = false;
                setCoveredAppointments(undefined);
                if (subscription.charge) {
                    // Always known now, not only once a shortfall is found: the per-row strippen
                    // display below (and `strips`) reads this regardless of whether the balance
                    // happens to cover everything.
                    setRequiredSubscription(subscription);
                    // One entry per appointment: the merged multi-appointment row stands for `qty`
                    // of them (each at `unit`), the per-line rows for one each. Counting the rows
                    // themselves would see a single row - and so a single appointment - for a
                    // booking of two, and the balance would look like it covers everything.
                    let appointmentPrices = _rows
                        .filter((row) => row.type === 'appointment')
                        .flatMap((row) => Array(row.qty).fill(row.unit)), requires = subscription.charge * appointmentPrices.length;
                    if (requires > subscription.balance) {
                        // todo: is a balance of -1 a thing?
                        _requiresAdditionalPayment = true;
                        setRequiresAdditionalPayment(true);
                        // An appointment is never split between subscription credit and cash -
                        // it is paid in full one way or the other. So only whole appointments
                        // the current balance can fully cover are "free" here; a leftover
                        // balance too small for one more whole appointment just stays on the
                        // card, unused, rather than partially discounting what is due in cash.
                        let coveredByBalance = Math.min(appointmentPrices.length, Math.floor(subscription.balance / subscription.charge)), uncoveredTotal = appointmentPrices.slice(coveredByBalance)
                            .reduce((sum, unit) => sum + unit, 0);
                        _subtotal = uncoveredTotal + _productsTotal;
                        setCoveredAppointments(coveredByBalance);
                        let requiredCredits = requires - subscription.balance, requiredSubscriptions = Math.ceil(requiredCredits / subscription.max);
                        setRequiredSubscriptions(requiredSubscriptions);
                        setRequiredSubscriptionsTotal(requiredSubscriptions * subscription.sellPrice);
                        if (paymentMethod.endsWith('addSubscription')) {
                            // every appointment ends up paid by subscription credit - the
                            // existing balance, topped up by the new card - so nothing is due in
                            // cash beyond buying the card, and the existing card contributes
                            // everything it has, not just what covers whole appointments
                            setChargeTotal(subscription.balance);
                            setInvoiceTotal(requiredSubscriptions * subscription.sellPrice + _productsTotal);
                        }
                        else {
                            // addPayment, or no variant chosen yet: whatever the card cannot
                            // cover in whole appointments is paid in full, in cash
                            setChargeTotal(coveredByBalance * subscription.charge);
                            setInvoiceTotal(_subtotal);
                        }
                    }
                    else {
                        setChargeTotal(requires);
                    }
                }
                else {
                    setChargeTotal(0);
                }
                if (!_requiresAdditionalPayment) {
                    // the credits cover every appointment, so the products are all that is left to pay
                    setInvoiceTotal(_productsTotal);
                    setInvoiceTotalMinusDiscounts(_productsTotal);
                }
                setShowDetails(true);
            }
            else {
                if (subscription) {
                    let requiredCredits = booking.appointments.length * subscription.charge, requiredSubscriptions = Math.ceil(requiredCredits / subscription.max);
                    setRequiredSubscription(subscription);
                    setRequiredSubscriptions(requiredSubscriptions);
                    setRequiredSubscriptionsTotal(requiredSubscriptions * subscription.sellPrice);
                    let total = ((subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId) ? 0 : ((_h = subscription === null || subscription === void 0 ? void 0 : subscription.sellPrice) !== null && _h !== void 0 ? _h : 0)) * requiredSubscriptions;
                    if (total < 0)
                        total = 0;
                    total += _productsTotal;
                    setInvoiceTotal(total);
                    setInvoiceTotalMinusDiscounts(total);
                    setAlternativePayment((_j = subscription === null || subscription === void 0 ? void 0 : subscription.title) !== null && _j !== void 0 ? _j : false);
                    setChargeTotal(0);
                    setShowDetails(total > 0);
                }
            }
        }
        else if (buyingSubscriptionOrMembership) {
            // Once "Strippenkaart/Abonnement aanschaffen" is confirmed, paymentMethod moves on to
            // the actual payment provider paying for that purchase (e.g. 'external', by way of a
            // brief 'alternate') - it no longer starts with 'subscription_'/'membership_', and the
            // subscription_ branch above parses the subscription id straight out of that string, so
            // it cannot run for this value either. What it already computed (chargeTotal,
            // invoiceTotal, ...) while paymentMethod still held that prefix is still correct - the
            // total does not change just because payment moved from "choosing" to "paying" - so
            // this branch deliberately does nothing rather than recompute it as a plain full-price
            // charge for the appointment on top of the purchase.
        }
        else if (paymentMethod === "alternate") {
            setShowDetails(false);
        }
        else {
            let total = (useDeposit && deposit ? deposit : _subtotal)
                + vatstotal(_rows)
                + feestotal(_rows);
            setInvoiceTotalMinusDiscounts(total);
            if (!useDeposit) {
                total -= discountstotal(_rows);
                if (total < 0)
                    total = 0;
            }
            setInvoiceTotal(total);
            setChargeTotal(0);
            if (_subtotal > 0)
                setShowDetails(true);
            setAlternativePayment(false);
            if (setSubTotal) {
                setSubTotal(total);
            }
        }
    }, [apptypes, resources, products, booking, cart, fees, deposit, useDeposit, paymentMethod, membershipMode, membershipCoverageKey]);
    useEffect(() => {
        if (setSubTotal) {
            setSubTotal(invoiceTotal);
        }
    }, [invoiceTotal]);
    useEffect(() => {
        if (setSubTotal) {
            setSubTotal(invoiceTotal);
        }
    }, []);
    const handleRowClick = (event) => {
        let value = event.currentTarget.getAttribute('rel');
        if (expanded === value) {
            setExpanded(false);
        }
        else {
            setExpanded(value);
        }
    };
    const deleteDiscount = (event) => {
        //@ts-ignore
        setCart(Object.assign(Object.assign({}, cart), { discounts: cart.discounts.filter(a => a.id !== event.target.closest('button').getAttribute('data-rel')) }));
    };
    const deleteGiftcard = (event) => {
        var _a;
        // uncheck, and remove it from the summary
        handleGiftcard(parseInt((_a = event.target.closest('button').getAttribute('data-rel')) !== null && _a !== void 0 ? _a : "0"), false);
    };
    const onSubmitVoucher = (data, event) => {
        let checkGift = cart.giftCards.find((giftcard) => { var _a; return giftcard.code.toUpperCase() === ((_a = data.code) === null || _a === void 0 ? void 0 : _a.toUpperCase().replace(' ', '')); });
        let checkCoupon = cart.discounts.find((discount) => { var _a; return discount.code.toUpperCase() === ((_a = data.code) === null || _a === void 0 ? void 0 : _a.toUpperCase().replace(' ', '')); });
        if (checkGift || checkCoupon) {
            setErrorValidate([t("Je hebt deze code al toegepast.", 'cartSummary')]);
        }
        else {
            if (!validateVoucherQuery.isFetching) {
                validateVoucherQuery.refetch();
            }
        }
    };
    const goTo = (e) => {
        let target = e.target.closest('button');
        let path = target.getAttribute('rel');
        if (onEdit) {
            onEdit(path);
            return;
        }
        navigate(`/oa_widget_${path}`);
    };
    const renderVats = useMemo(() => {
        return (_jsx(_Fragment, { children: vats === null || vats === void 0 ? void 0 : vats.map((value, i) => {
                return (_jsxs(TableRow, { sx: {
                        backgroundColor: theme.palette.background.default
                    }, children: [_jsx(TableCell, { children: t('BTW', 'cartSummary') }), _jsx(TableCell, { align: "right", children: `${(value.rate).toFixed(0)} %` }), _jsx(TableCell, { align: "right", children: showMoney(value.total) }), isLarge && (_jsx(TableCell, {}))] }, i));
            }) }));
    }, [vats]);
    const renderDiscounts = useMemo(() => {
        if (!cart || !cart.discounts || alternativePayment) {
            return (_jsx(_Fragment, {}));
        }
        let remaining = invoiceTotalMinusDiscounts;
        // make sure we use the coupons first, then use giftcards for anything remaining
        let _discounts = cart.discounts.sort((a, b) => {
            return a.type === 'discount' && b.type !== 'discount' ? -1 : 1;
        });
        return (_jsx(_Fragment, { children: _discounts === null || _discounts === void 0 ? void 0 : _discounts.map((discount, i) => {
                var _a;
                let value = discount.type === "giftCard" ? ((_a = discount.balance) !== null && _a !== void 0 ? _a : 0) : discount.value;
                if (value > remaining)
                    value = remaining;
                remaining -= value;
                if (discount.type === 'giftCard') {
                    if (!canRedeem) {
                        return _jsx(Fragment, {}, i);
                    }
                    let usable = giftcards === null || giftcards === void 0 ? void 0 : giftcards.find((giftcard) => giftcard.id === discount.id), 
                    // code: string = usable ? '' : `( ${discount.code} )`
                    code = discount.code !== '' && !discount.code.startsWith('automatic_') ? `(${discount.code})` : '';
                    return (_jsxs(TableRow, { sx: {
                            backgroundColor: theme.palette.background.default
                        }, children: [_jsx(TableCell, { sx: {
                                    width: '1em',
                                    padding: 0
                                }, children: _jsx(IconButton, { onClick: deleteGiftcard, "data-rel": discount.id, "aria-label": "delete", children: _jsx(DeleteIcon, { sx: { color: theme.palette.text.secondary } }) }) }), _jsxs(TableCell, { colSpan: locked ? (isLarge ? 3 : 2) : (isLarge ? 2 : 1), children: [discount.title, " ", code] }), _jsxs(StyledTableCell, { children: ["- ", showMoney(value)] })] }, i));
                }
                if (discount.type === 'discount' && value !== null && value > 0) {
                    let code = discount.code.startsWith('discountType_') || discount.code.startsWith('automatic_') ? '' : `(${discount.code})`;
                    if (!canRedeem && code !== "") {
                        return _jsx(Fragment, {}, i);
                    }
                    // we don't allow (automatic or not) discounts if we are buying a subscription to use as payment for the appointment
                    if (cart.lines.find((line) => ["subscription", "membership"].includes(line.productType))) {
                        return _jsx(Fragment, {}, i);
                    } // else is display automatic discounts whether redeem is enabled or not
                    return (_jsxs(TableRow, { sx: {
                            backgroundColor: theme.palette.background.default
                        }, children: [!discount.code.startsWith('discountType_') && (_jsx(TableCell, { sx: {
                                    width: '1em',
                                    padding: 0
                                }, children: _jsx(IconButton, { onClick: deleteDiscount, "data-rel": discount.id, "aria-label": "delete", children: _jsx(DeleteIcon, { sx: { color: theme.palette.text.secondary } }) }) })), _jsxs(TableCell, { colSpan: discount.code.startsWith('discountType_') ? (isLarge ? 3 : 2) : (isLarge ? 2 : 1), children: [discount.title, " ", code] }), _jsxs(StyledTableCell, { children: ["- ", showMoney(value)] })] }, i));
                }
            }) }));
    }, [cart, cart.giftCards, giftcards, alternativePayment, invoiceTotalMinusDiscounts, canRedeem]);
    const handleGiftcard = (event, checked) => {
        var _a, _b;
        let discountId = typeof event === "number" ? event : parseInt((_b = (_a = event.target.closest('.MuiCheckbox-root')) === null || _a === void 0 ? void 0 : _a.getAttribute('rel')) !== null && _b !== void 0 ? _b : "0");
        let existing = cart.discounts.find((discount) => {
            return parseInt(discount.id) === discountId;
        });
        if (!existing && checked) {
            let discount = giftcards === null || giftcards === void 0 ? void 0 : giftcards.find((discount) => parseInt(discount.id) === discountId);
            if (discount) {
                setCart(Object.assign(Object.assign({}, cart), { discounts: [...cart.discounts, discount] }));
            }
        }
        if (existing && !checked) {
            setCart(Object.assign(Object.assign({}, cart), { discounts: cart.discounts.filter(discount => parseInt(discount.id) !== discountId) }));
        }
    };
    const renderGiftcards = useMemo(() => {
        if (!cart || !cart.discounts || alternativePayment) {
            return (_jsx(_Fragment, {}));
        }
        return (_jsx(_Fragment, { children: giftcards && (giftcards === null || giftcards === void 0 ? void 0 : giftcards.length) > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h6", sx: {
                            color: theme.palette.text.primary,
                            marginLeft: 1,
                            marginTop: 0,
                            fontSize: '1rem'
                        }, children: t('Jouw cadeaukaarten', 'cartSummary') }), _jsx(TableContainer, { component: Paper, sx: {
                            marginBottom: 4
                        }, children: _jsx(Table, { sx: { color: theme.palette.text.primary }, "aria-label": t('Jouw cadeaukaarten', 'cartSummary'), children: _jsx(TableBody, { sx: {
                                    "& .MuiTableCell-body": {
                                        fontSize: '1rem'
                                    }
                                }, children: giftcards === null || giftcards === void 0 ? void 0 : giftcards.map((discount, i) => {
                                    var _a;
                                    let value = (_a = discount.balance) !== null && _a !== void 0 ? _a : 0, checked = cart.discounts.map((discount) => discount.id).includes(discount.id), codeStr = discount.code !== '' && !discount.code.startsWith('automatic_') ? `(${discount.code})` : '';
                                    //if(value > invoiceTotal) value = invoiceTotal
                                    return (_jsxs(TableRow, { sx: {
                                            backgroundColor: theme.palette.background.default
                                        }, children: [_jsx(TableCell, { sx: {
                                                    width: '1em',
                                                    padding: 0
                                                }, children: _jsx(Checkbox, { disabled: !checked && invoiceTotal === 0, onChange: handleGiftcard, rel: discount.id, checked: checked, color: "default" }) }), _jsxs(TableCell, { children: [discount.title, " ", codeStr] }), _jsx(StyledTableCell, { children: showMoney(value) })] }, i));
                                }) }) }) })] })) }));
    }, [cart, cart.giftCards, alternativePayment, giftcards, booking.appointments, invoiceTotal, errorValidate, successValidate]);
    const renderRedeem = useMemo(() => {
        return (_jsx(_Fragment, { children: hasCheckout && invoiceTotal > 0 && showDetails && (_jsx(_Fragment, { children: _jsx(FormContainer, { formContext: formContextVoucher, onSuccess: onSubmitVoucher, onError: onError, children: _jsxs(FormErrorProvider, { onError: (error) => {
                            if (error.type === 'validate') {
                                console.log(error);
                            }
                            return t(error.type, 'error');
                        }, children: [_jsx(Typography, { variant: "h6", sx: {
                                    color: theme.palette.text.primary,
                                    marginLeft: 1,
                                    marginTop: 0,
                                    fontSize: '1rem'
                                }, children: t('Wil je een cadeaukaart of korting gebruiken?', 'cartSummary') }), _jsx(CustomTextFieldElement, Object.assign({ required: false, label: t('Kortingscode of cadeaukaart code', 'cartSummary'), type: "code" }, register('code'), { InputProps: {
                                    startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(DiscountIcon, { sx: { color: theme.palette.text.secondary } }) }))
                                }, sx: { my: 1, marginBottom: 4 }, fullWidth: true })), hasCode && (_jsx(Button, { type: "submit", fullWidth: true, variant: "contained", sx: { marginBottom: 4 }, children: t('Code toevoegen', 'cartSummary') }))] }) }) })) }));
    }, [canRedeem, useDeposit, paymentMethod, cart, alternativePayment, giftcards, booking.appointments, invoiceTotal, hasCode, errorValidate, successValidate]);
    const renderFees = useMemo(() => {
        if (!fees || fees.length === 0) {
            return (_jsx(_Fragment, {}));
        }
        return (_jsx(_Fragment, { children: fees.map((fee, i) => {
                return (_jsxs(TableRow, { sx: {
                        backgroundColor: theme.palette.background.default
                    }, children: [_jsx(TableCell, { colSpan: locked ? (isLarge ? 3 : 2) : (isLarge ? 3 : 2), children: fee.name }), _jsx(StyledTableCell, { children: showMoney(fee.value) })] }, i));
            }) }));
    }, [fees]);
    // const priceRow = (qty: number, unit: number): number => {
    //     return qty * unit
    // }
    // const createRow = (desc: string[], unit: number, qty: number, vatrate: number, type: ProductType): Row => {
    //     let price: number = priceRow(qty ?? 1, unit ?? 0)
    //     return {
    //         desc: desc,
    //         unit: unit ?? 0,
    //         qty: qty ?? 1,
    //         price: price,
    //         vatrate: vatrate,
    //         type: type
    //     } as Row
    // }
    // const subtotal = (items: readonly Row[]): number => {
    //     return items.map(({ price }) => price).reduce((sum, i) => sum + i, 0)
    // }
    const vatstotal = (items) => {
        let total = 0;
        total = items.map(({ price, vatrate, qty }) => {
            let rowVat = price * qty * (vatrate / 100);
            // for now just 1 product in the cart
            setVats([{ rate: vatrate, total: rowVat }]);
            return rowVat;
        }).reduce((sum, i) => sum + i, 0);
        return total;
    };
    const discountstotal = (items) => {
        let total = 0;
        if (cart.lines.find((line) => ["subscription", "membership"].includes(line.productType))) {
            return total;
        }
        if (cart && cart.discounts && !alternativePayment) {
            total = cart.discounts.map((discount) => {
                var _a;
                return discount.type === "giftCard" ? ((_a = discount.balance) !== null && _a !== void 0 ? _a : 0) : discount.value;
            }).reduce((sum, i) => sum + i, 0);
        }
        return total;
    };
    const feestotal = (items) => {
        let total = 0;
        if (fees) {
            total = fees.map((fee) => {
                return fee.value;
            }).reduce((sum, i) => sum + i, 0);
        }
        return total;
    };
    const onError = (errors) => { };
    rows.sort((a, b) => { return a.type === "appointment" ? -1 : 1; });
    // A strippenkaart that cannot cover every appointment is preselected, but what is owed depends on which
    // of its variants ("_addSubscription" / "_addPayment") the consumer picks. Until they do, no line of the
    // cart says how anything is paid.
    const awaitsVariantChoice = !!(paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('subscription_'))
        && !paymentMethod.endsWith('addSubscription')
        && !paymentMethod.endsWith('addPayment')
        && coveredAppointments !== undefined;
    // The lines under an appointment row: how many appointments each stands for and what they cost. Paid by
    // subscription an appointment costs strippen, in full - never a share of one - so when the card only
    // covers some of them the row is split: the covered ones in strippen, the rest in what they cost in
    // cash, or, when a new card is bought for them, in the strippen that card supplies plus its price.
    const detailLines = (row, index) => {
        var _a, _b;
        if ((membershipMode === 'exceeded' || membershipMode === 'addPayment') && membershipCoverage) {
            // One line per appointment, as for subscriptions, whose last column says how many strippen it costs.
            // A membership has none to count, so for the appointments it pays for that says it is the membership.
            // The others show what they cost, when they are paid online, or that the maximum is reached, when
            // nothing can pay for them.
            let before = rows.slice(0, index).reduce((sum, other) => sum + (other.type === 'appointment' ? other.qty : 0), 0);
            // in the order of the appointments in time, not of the order they were added to the cart: the limits
            // are counted that way too, so the lines that are over the limit are the last ones
            return Array.from({ length: row.qty }, (_, n) => {
                var _a, _b;
                let i = before + n, appointment = booking.appointments[i], covered = (_a = membershipCoverage.covered[i]) !== null && _a !== void 0 ? _a : true;
                return {
                    qty: 1,
                    time: appointment ? dayjs(appointment.date).valueOf() : 0,
                    amount: covered
                        ? t('abonnement', 'cartSummary')
                        : (membershipMode === 'exceeded'
                            // what stands in the way of booking, so it is the one thing in the table that is not to be missed
                            ? _jsx(Box, { component: "span", sx: { color: theme.palette.error.main, fontWeight: 700 }, children: t('max bereikt', 'cartSummary') })
                            : showMoney((_b = appointment === null || appointment === void 0 ? void 0 : appointment.price) !== null && _b !== void 0 ? _b : row.unit)),
                    buysCard: false,
                    // which appointment the line is about: its date and time, and what it was booked for
                    label: appointment
                        ? _jsxs(_Fragment, { children: [_jsx(Box, { component: "span", sx: { display: 'block' }, children: dayjs(appointment.date).locale(language).format('dddd D MMMM HH:mm') }), _jsx(Box, { component: "span", sx: { display: 'block', color: theme.palette.text.secondary }, children: apptypeNamesFor(appointment, apptypes).join(', ') })] })
                        : row.desc[row.qty]
                };
            }).sort((a, b) => a.time - b.time);
        }
        if (awaitsVariantChoice) {
            // nothing is known about how each appointment is paid yet, so they stay one line, unpriced
            return [{ qty: row.qty, amount: '', buysCard: false }];
        }
        // paymentMethod's own prefix says which kind while the consumer is still choosing; once
        // they commit, it moves on to the actual payment provider and no longer says so at all
        // (see buyingSubscriptionOrMembership above) - requiredSubscription.type is what still
        // knows, since it is set by both the subscription_ and membership_ branches above.
        const subscriptionKind = (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('subscription_')) ? 'subscription'
            : (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('membership_')) ? 'membership'
                : buyingSubscriptionOrMembership ? ((_a = requiredSubscription === null || requiredSubscription === void 0 ? void 0 : requiredSubscription.type) !== null && _a !== void 0 ? _a : 'subscription')
                    : 'none';
        if (subscriptionKind === 'none') {
            return [{ qty: row.qty, amount: showMoney(row.unit), buysCard: false }];
        }
        if (subscriptionKind === 'membership') {
            // a membership covers the appointment outright - there is no balance or strip count to
            // show, just that it is the membership paying for it, same wording as the existing-
            // membership-over-its-limit case above
            return [{ qty: row.qty, amount: t('abonnement', 'cartSummary'), buysCard: false }];
        }
        let charge = (_b = requiredSubscription === null || requiredSubscription === void 0 ? void 0 : requiredSubscription.charge) !== null && _b !== void 0 ? _b : 1, strips = (count) => `${count} ${t(count === 1 ? 'strip' : 'strippen')}`, before = rows.slice(0, index).reduce((sum, other) => sum + (other.type === 'appointment' ? other.qty : 0), 0), covered = coveredAppointments !== null && coveredAppointments !== void 0 ? coveredAppointments : Infinity, coveredHere = Math.min(row.qty, Math.max(0, covered - before)), lines = [];
        if (coveredHere > 0) {
            lines.push({ qty: coveredHere, amount: strips(charge), buysCard: false });
        }
        if (coveredHere < row.qty) {
            if ((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.endsWith('addSubscription')) && requiredSubscription) {
                // the card is bought once, for the appointments that come first in the uncovered ones
                let neededStrips = Math.max(0, charge * booking.appointments.length - requiredSubscription.balance), isFirst = before + coveredHere === covered;
                lines.push({
                    qty: row.qty - coveredHere,
                    amount: isFirst ? `${strips(neededStrips)} + ${moneyFormat(requiredSubscriptionsTotal)}` : '',
                    buysCard: isFirst
                });
            }
            else {
                lines.push({ qty: row.qty - coveredHere, amount: showMoney(row.unit), buysCard: false });
            }
        }
        return lines;
    };
    return (_jsxs(_Fragment, { children: [_jsx(TableContainer, { component: Paper, sx: {
                    marginBottom: 4
                }, children: _jsx(Table, { "aria-label": t('Overzicht van je reservering', 'cartSummary'), children: _jsxs(TableBody, { sx: {
                            "& .MuiTableCell-body": {
                                fontSize: '1rem'
                            }
                        }, children: [rows === null || rows === void 0 ? void 0 : rows.map((row, i) => {
                                var _a;
                                return (_jsxs(Fragment, { children: [_jsxs(TableRow, { rel: i.toString(), onClick: handleRowClick, sx: {
                                                backgroundColor: theme.palette.background.default
                                            }, children: [_jsx(TableCell, { colSpan: row.type === 'appointment' ? (isLarge ? 3 : 4) : 2, children: ((_a = row.header) !== null && _a !== void 0 ? _a : row.desc.map((line, i) => ({
                                                        text: line,
                                                        bold: row.type === 'appointment' && i < booking.appointments.length
                                                    }))).map((line, i) => _jsx(Box, { sx: Object.assign({ fontSize: row.type === 'appointment' ? '1.25rem' : '1rem' }, (line.bold && {
                                                            fontWeight: 700
                                                        })), children: line.text }, i)) }), row.type !== 'appointment' && row.qty > 0 && (_jsxs(_Fragment, { children: [_jsx(StyledTableCell, { sx: { minWidth: '2em' }, children: row.type === 'product'
                                                                // ? <OutlinedInput value={row.qty} type={"number"} sx={{ width: '3.5em' }} /> 
                                                                ? row.qty
                                                                : row.qty }), _jsx(StyledTableCell, { children: showMoney(row.price) })] })), isLarge && row.type === 'appointment' && (_jsx(TableCell, { sx: { width: '2em' }, align: "right", children: _jsx(_Fragment, { children: !locked && (_jsx(IconButton, { onClick: goTo, rel: 'index', "aria-label": "edit", size: "medium", children: _jsx(EditIcon, { sx: { color: theme.palette.text.secondary } }) })) }) }))] }, row.desc), row.type === 'appointment' && (_jsxs(_Fragment, { children: [!row.unpaid && detailLines(row, i).map((line, lineIndex) => {
                                                    var _a;
                                                    return (_jsxs(TableRow, { sx: {
                                                            backgroundColor: theme.palette.background.default
                                                        }, children: [_jsxs(TableCell, { colSpan: 2, children: [(_a = line.label) !== null && _a !== void 0 ? _a : row.desc[row.qty], line.buysCard && (requiredSubscription === null || requiredSubscription === void 0 ? void 0 : requiredSubscription.title) && (_jsx(Box, { sx: { fontSize: '0.875rem', color: theme.palette.text.secondary }, children: requiredSubscription.title }))] }), _jsx(StyledTableCell, { children: showQty(line.qty) }), _jsx(StyledTableCell, { sx: { minWidth: '6em' }, children: line.amount })] }, lineIndex));
                                                }), requiredSubscriptions > 0 && ((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('subscription')) || (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('membership'))) && !(paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.endsWith('addSubscription')) && !(paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.endsWith('addPayment')) && !awaitsVariantChoice && (_jsxs(TableRow, { sx: {
                                                        backgroundColor: theme.palette.background.default
                                                    }, children: [_jsx(TableCell, { colSpan: 2, children: requiredSubscription === null || requiredSubscription === void 0 ? void 0 : requiredSubscription.title }), _jsx(StyledTableCell, { children: showQty(requiredSubscriptions) }), _jsx(StyledTableCell, { children: showMoney(requiredSubscriptionsTotal) })] }))] })), !isLarge && expanded === i.toString() && (_jsxs(TableRow, { sx: {
                                                backgroundColor: theme.palette.background.default
                                            }, children: [row.type === 'product' && (_jsx(_Fragment, {})
                                                // <TableCell colSpan={3} sx={{ padding: 0 }}>
                                                //     <Button fullWidth sx={{ marginBottom: 2 }} variant={"contained"} color={"error"}>{t('Product verwijderen')}</Button>
                                                // </TableCell>
                                                ), row.type === 'appointment' && (_jsx(TableCell, { colSpan: 3, sx: { padding: 0 }, children: _jsx(Button, { fullWidth: true, onClick: goTo, rel: 'index', sx: { marginBottom: 2 }, variant: "contained", color: "error", children: t('Afspraak wijzigen', 'cartSummary') }) }))] }))] }, i));
                            }), chargeTotal == 0 && showDetails && invoiceTotalMinusDiscounts > 0 && (_jsxs(_Fragment, { children: [!alternativePayment && invoiceSubtotal > 0 && membershipMode !== 'addPayment' && (_jsxs(TableRow, { sx: {
                                            color: theme.palette.secondary.contrastText,
                                            backgroundColor: theme.palette.secondary.main,
                                        }, children: [_jsx(TableCell, { colSpan: isLarge ? 3 : 3, children: t('Totaal', 'cartSummary') }), _jsx(StyledTableCell, { children: showMoney(invoiceSubtotal) })] })), alternativePayment && invoiceSubtotal > 0 && (_jsxs(TableRow, { sx: {
                                            color: theme.palette.secondary.contrastText,
                                            backgroundColor: theme.palette.secondary.main,
                                        }, children: [_jsx(TableCell, { colSpan: isLarge ? 3 : 3, children: t('Totaal', 'cartSummary') }), _jsx(StyledTableCell, { children: showMoney(invoiceTotal) })] })), !alternativePayment && useDeposit && deposit && deposit > 0 && (_jsxs(TableRow, { sx: {
                                            color: theme.palette.secondary.contrastText,
                                            backgroundColor: theme.palette.secondary.main,
                                        }, children: [_jsx(TableCell, { colSpan: isLarge ? 3 : 3, children: t('Aanbetaling', 'cartSummary') }), _jsx(StyledTableCell, { children: showMoney(deposit) })] })), renderFees, !useDeposit && (_jsx(_Fragment, { children: renderDiscounts })), _jsxs(TableRow, { sx: {
                                            color: theme.palette.secondary.contrastText,
                                            backgroundColor: theme.palette.secondary.main
                                        }, children: [_jsx(TableCell, { colSpan: isLarge ? 3 : 3, sx: {
                                                    fontWeight: 'bold'
                                                }, children: t('Nog te betalen', 'cartSummary') }), _jsx(StyledTableCell, { align: "right", sx: {
                                                    fontWeight: 'bold',
                                                }, children: showMoney(invoiceTotal) })] })] })), chargeTotal > 0 && showDetails && (_jsxs(TableRow, { sx: {
                                    color: theme.palette.secondary.contrastText,
                                    backgroundColor: theme.palette.secondary.main
                                }, children: [_jsx(TableCell, { colSpan: isLarge ? 2 : 2, sx: {
                                            fontWeight: 'bold'
                                        }, children: t('Nog te betalen', 'cartSummary') }), _jsxs(StyledTableCell, { align: "right", colSpan: 2, sx: {
                                            fontWeight: 'bold'
                                        }, children: [chargeTotal, " ", t(chargeTotal === 1 ? 'strip' : 'strippen'), requiresAdditionalPayment && (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.endsWith('addSubscription')) && requiredSubscription && (
                                            // buying a new card: it isn't only chargeTotal (the existing
                                            // card's contribution) that is owed in strippen - the new
                                            // card's own contribution is too, spelled out separately so
                                            // "8 strippen" doesn't read as the whole story when a further
                                            // 4 are about to come from the card being bought
                                            _jsxs(_Fragment, { children: [' + ', (requiredSubscription.charge * booking.appointments.length) - chargeTotal, " ", t('strippen')] })), invoiceTotal > 0 && _jsxs(_Fragment, { children: ["\u00A0+ ", moneyFormat(invoiceTotal)] })] })] })), canDeposit && !onlyDeposit && setter && (_jsx(TableRow, { sx: {
                                    backgroundColor: theme.palette.background.default
                                }, children: _jsx(TableCell, { colSpan: isLarge ? 4 : 3, sx: { padding: 0 }, children: _jsx(FormContainer, { children: _jsx(FormErrorProvider, { onError: (error) => {
                                                if (error.type === 'validate') {
                                                    console.log(error);
                                                }
                                                return t(error.type, 'error');
                                            }, children: _jsx(CustomCheckboxElement, { sx: {
                                                    marginLeft: 1
                                                }, labelProps: {
                                                    sx: {
                                                        color: theme.palette.text.primary,
                                                        "& a": {
                                                            color: theme.palette.text.primary,
                                                        }
                                                    }
                                                }, onChange: (event, checked) => {
                                                    setter(checked);
                                                }, required: false, label: t('Alleen aanbetalen', 'cartSummary').concat(showMoney(deposit) ? ` (${showMoney(deposit)})` : ''), name: 'useDeposit' }) }) }) }) }))] }) }) }), canRedeem && !useDeposit && invoiceTotal > 0 && (alternativePayment || (!(paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('membership')) && !(paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('subscription')) && paymentMethod !== "alternate")) && (_jsxs(_Fragment, { children: [renderRedeem, renderGiftcards] }))] }));
}
